import { Controller } from '@hotwired/stimulus'
import catalogState from '../stores/catalog_state'
import { showPreloader } from '../utils/preloader'

// Задержка перед перезагрузкой: пользователь мог стереть запрос, чтобы
// сразу напечатать новый — не дёргаем страницу, пока он печатает.
const REFRESH_DELAY = 600
const SUGGESTIONS_DEBOUNCE = 250

/**
 * @file Stimulus-контроллер формы серверного поиска (тренеры, продукты).
 *
 * - `changed`: когда поле очищено (кнопкой сброса или вручную), в URL есть
 *   поисковый query-параметр и клиентские фильтры каталога не выбраны —
 *   перезагружает страницу без параметра, возвращая полный список.
 * - `submit`: с пустым обязательным полем показывает стилизованную ошибку
 *   компонента input («This field is required») вместо нативного тултипа
 *   (форме нужен novalidate); при валидном сабмите включает полноэкранный
 *   прелоудер на время серверного поиска.
 *
 * Прелоудер прячется сам: Turbo заменяет body при рендере ответа.
 */
export default class SearchFormController extends Controller {
  static targets = ['input']

  static values = {
    param: String,
    loadingLabel: String,
    suggestionsUrl: String
  }

  #timer = null
  #suggestionsTimer = null
  #suggestionsRequestId = 0
  #dropdown = null
  #suggestions = []
  #activeIndex = -1
  #onKeydown = null
  #onBlur = null

  connect() {
    if (!this.hasSuggestionsUrlValue || !this.hasInputTarget) return

    this.#onKeydown = (event) => this.#handleSuggestionsKeydown(event)
    this.#onBlur = () => setTimeout(() => this.#closeSuggestions(), 150)
    this.inputTarget.addEventListener('keydown', this.#onKeydown)
    this.inputTarget.addEventListener('blur', this.#onBlur)
  }

  disconnect() {
    clearTimeout(this.#timer)
    clearTimeout(this.#suggestionsTimer)
    this.#suggestionsRequestId++

    if (this.hasInputTarget) {
      if (this.#onKeydown)
        this.inputTarget.removeEventListener('keydown', this.#onKeydown)
      if (this.#onBlur)
        this.inputTarget.removeEventListener('blur', this.#onBlur)
    }

    this.#dropdown?.remove()
  }

  changed(event) {
    clearTimeout(this.#timer)

    // Ошибка «обязательное поле» показывается только по сабмиту —
    // как только пользователь редактирует поле, прячем её.
    this.#inputController(event.target)?.reset()

    const query = event.target.value.trim()
    if (this.hasSuggestionsUrlValue) this.#scheduleSuggestions(query)
    if (query !== '') return

    this.#timer = setTimeout(() => this.#refresh(), REFRESH_DELAY)
  }

  submit(event) {
    const field = this.element.querySelector('input[data-input-target="input"]')

    if (field && field.required && field.value.trim() === '') {
      event.preventDefault()
      this.#inputController(field)?.validate()
      field.focus()
      return
    }

    showPreloader({ label: this.loadingLabelValue })
  }

  #inputController(field) {
    const inputElement = field.closest('[data-controller~="input"]')
    if (!inputElement) return null

    return this.application.getControllerForElementAndIdentifier(
      inputElement,
      'input'
    )
  }

  #scheduleSuggestions(query) {
    clearTimeout(this.#suggestionsTimer)

    if (query === '') {
      this.#suggestionsRequestId++
      this.#closeSuggestions()
      return
    }

    const requestId = ++this.#suggestionsRequestId
    this.#suggestionsTimer = setTimeout(
      () => this.#fetchSuggestions(query, requestId),
      SUGGESTIONS_DEBOUNCE
    )
  }

  async #fetchSuggestions(query, requestId) {
    const url = new URL(this.suggestionsUrlValue, window.location.origin)
    url.searchParams.set('q', query)

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (!response.ok)
        throw new Error(`Suggestions request failed: ${response.status}`)

      const data = await response.json()
      if (requestId !== this.#suggestionsRequestId) return

      // `items` — общий контракт подсказок (статьи и всё, что появится
      // дальше). `products` оставлен, потому что products#suggestions уже
      // отдаёт этот ключ, и менять его ради переименования смысла нет.
      const items = Array.isArray(data.items) ? data.items : data.products
      this.#suggestions = Array.isArray(items) ? items : []
      this.#renderSuggestions()
    } catch {
      if (requestId === this.#suggestionsRequestId) this.#closeSuggestions()
    }
  }

  #renderSuggestions() {
    if (this.#suggestions.length === 0) {
      this.#closeSuggestions()
      return
    }

    const dropdown = this.#ensureDropdown()
    dropdown.innerHTML = ''
    this.#activeIndex = -1

    this.#suggestions.forEach((suggestion, index) => {
      const item = document.createElement('a')
      item.className = 'pac-item product-search-suggestion'
      item.href = suggestion.url
      item.setAttribute('role', 'option')
      item.setAttribute('aria-selected', 'false')

      if (suggestion.thumbnail_url) {
        const image = document.createElement('img')
        image.className = 'product-search-suggestion__image'
        image.src = suggestion.thumbnail_url
        image.alt = ''
        image.width = 40
        image.height = 40
        item.appendChild(image)
      }

      const name = document.createElement('span')
      name.textContent = suggestion.name
      item.appendChild(name)

      item.addEventListener('mousedown', (event) => event.preventDefault())
      item.addEventListener('click', (event) => {
        event.preventDefault()
        this.#visitSuggestion(index)
      })
      dropdown.appendChild(item)
    })

    dropdown.style.display = 'flex'
    this.inputTarget.setAttribute('aria-expanded', 'true')
  }

  #ensureDropdown() {
    if (this.#dropdown) return this.#dropdown

    const container =
      this.inputTarget.closest('.input') || this.inputTarget.parentElement
    if (getComputedStyle(container).position === 'static')
      container.style.position = 'relative'

    const dropdown = document.createElement('div')
    dropdown.className = 'pac-container product-search-suggestions'
    dropdown.setAttribute('role', 'listbox')
    dropdown.style.position = 'absolute'
    dropdown.style.top = '100%'
    dropdown.style.left = '0'
    dropdown.style.right = '0'
    dropdown.style.zIndex = '1000'
    dropdown.style.display = 'none'
    container.appendChild(dropdown)

    this.#dropdown = dropdown
    return dropdown
  }

  #closeSuggestions() {
    this.#suggestions = []
    this.#activeIndex = -1
    if (this.#dropdown) {
      this.#dropdown.style.display = 'none'
      this.#dropdown.innerHTML = ''
    }
    if (this.hasInputTarget)
      this.inputTarget.setAttribute('aria-expanded', 'false')
  }

  #handleSuggestionsKeydown(event) {
    if (this.#suggestions.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.#moveActiveSuggestion(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.#moveActiveSuggestion(-1)
        break
      case 'Enter':
        if (this.#activeIndex >= 0) {
          event.preventDefault()
          this.#visitSuggestion(this.#activeIndex)
        }
        break
      case 'Escape':
        this.#closeSuggestions()
        break
    }
  }

  #moveActiveSuggestion(delta) {
    const count = this.#suggestions.length
    this.#activeIndex = (this.#activeIndex + delta + count) % count

    const items = this.#dropdown?.querySelectorAll('.pac-item') || []
    items.forEach((item, index) => {
      const active = index === this.#activeIndex
      item.classList.toggle('pac-item--active', active)
      item.setAttribute('aria-selected', active ? 'true' : 'false')
    })
  }

  #visitSuggestion(index) {
    const suggestion = this.#suggestions[index]
    if (!suggestion?.url) return

    clearTimeout(this.#suggestionsTimer)
    this.#suggestionsRequestId++
    this.#closeSuggestions()
    Turbo.visit(suggestion.url)
  }

  #refresh() {
    if (Object.keys(catalogState.filters).length > 0) return

    const url = new URL(window.location.href)
    if (!url.searchParams.has(this.paramValue)) return

    url.searchParams.delete(this.paramValue)
    showPreloader({ label: this.loadingLabelValue })
    Turbo.visit(url.toString())
  }
}
